// UI strings. The console picks GRID_LOCALE_TH / GRID_LOCALE_EN from its own i18n.
export interface GridLocaleText {
  toolbarColumns: string;
  toolbarFilters: string;
  toolbarDensity: string;
  toolbarExport: string;
  quickFilterPlaceholder: string;
  quickFilterClear: string;
  density: { compact: string; standard: string; comfortable: string };
  exportCsv: string;
  exportSelected: (n: number) => string;
  columnsPanelSearch: string;
  columnsPanelShowAll: string;
  columnsPanelHideAll: string;
  columnsPanelReset: string;
  filterPanelAdd: string;
  filterPanelRemoveAll: string;
  filterPanelColumn: string;
  filterPanelOperator: string;
  filterPanelValue: string;
  filterPanelLogic: string;
  filterPanelAnd: string;
  filterPanelOr: string;
  filterPanelDelete: string;
  filterPanelEmpty: string;
  filterValueAny: string;
  filterValueTrue: string;
  filterValueFalse: string;
  operators: Record<string, string>;
  columnMenu: (column: string) => string;
  columnMenuSortAsc: string;
  columnMenuSortDesc: string;
  columnMenuUnsort: string;
  columnMenuFilter: string;
  columnMenuHide: string;
  columnMenuManage: string;
  columnMenuPinLeft: string;
  columnMenuPinRight: string;
  columnMenuUnpin: string;
  columnResize: (column: string) => string;
  noRows: string;
  noResults: string;
  loading: string;
  selectedRows: (n: number) => string;
  rowsPerPage: string;
  displayedRows: (from: number, to: number, count: number | undefined, hasMore: boolean) => string;
  firstPage: string;
  previousPage: string;
  nextPage: string;
  lastPage: string;
  selectAll: string;
  selectRow: string;
  expandRow: string;
  collapseRow: string;
  activeFilters: (n: number) => string;
}

const OPERATORS_EN: Record<string, string> = {
  contains: "contains",
  equals: "equals",
  startsWith: "starts with",
  endsWith: "ends with",
  isEmpty: "is empty",
  isNotEmpty: "is not empty",
  "=": "=",
  "!=": "≠",
  ">": ">",
  ">=": "≥",
  "<": "<",
  "<=": "≤",
  is: "is",
  not: "is not",
  after: "is after",
  onOrAfter: "is on or after",
  before: "is before",
  onOrBefore: "is on or before",
  isAnyOf: "is any of",
};

const OPERATORS_TH: Record<string, string> = {
  contains: "มีคำว่า",
  equals: "เท่ากับ",
  startsWith: "ขึ้นต้นด้วย",
  endsWith: "ลงท้ายด้วย",
  isEmpty: "ว่าง",
  isNotEmpty: "ไม่ว่าง",
  "=": "=",
  "!=": "≠",
  ">": ">",
  ">=": "≥",
  "<": "<",
  "<=": "≤",
  is: "เป็น",
  not: "ไม่เป็น",
  after: "หลัง",
  onOrAfter: "ตั้งแต่",
  before: "ก่อน",
  onOrBefore: "ไม่เกิน",
  isAnyOf: "เป็นอย่างใดอย่างหนึ่ง",
};

export const GRID_LOCALE_EN: GridLocaleText = {
  toolbarColumns: "Columns",
  toolbarFilters: "Filters",
  toolbarDensity: "Density",
  toolbarExport: "Export",
  quickFilterPlaceholder: "Search…",
  quickFilterClear: "Clear search",
  density: { compact: "Compact", standard: "Standard", comfortable: "Comfortable" },
  exportCsv: "Download as CSV",
  exportSelected: (n) => `Download ${n} selected as CSV`,
  columnsPanelSearch: "Find column",
  columnsPanelShowAll: "Show all",
  columnsPanelHideAll: "Hide all",
  columnsPanelReset: "Reset layout",
  filterPanelAdd: "Add filter",
  filterPanelRemoveAll: "Remove all",
  filterPanelColumn: "Column",
  filterPanelOperator: "Operator",
  filterPanelValue: "Value",
  filterPanelLogic: "Logic",
  filterPanelAnd: "And",
  filterPanelOr: "Or",
  filterPanelDelete: "Delete filter",
  filterPanelEmpty: "No filters",
  filterValueAny: "Any",
  filterValueTrue: "Yes",
  filterValueFalse: "No",
  operators: OPERATORS_EN,
  columnMenu: (c) => `${c} column menu`,
  columnMenuSortAsc: "Sort ascending",
  columnMenuSortDesc: "Sort descending",
  columnMenuUnsort: "Unsort",
  columnMenuFilter: "Filter",
  columnMenuHide: "Hide column",
  columnMenuManage: "Manage columns",
  columnMenuPinLeft: "Pin to left",
  columnMenuPinRight: "Pin to right",
  columnMenuUnpin: "Unpin",
  columnResize: (c) => `Resize ${c}`,
  noRows: "No rows",
  noResults: "No results found",
  loading: "Loading…",
  selectedRows: (n) => `${n.toLocaleString()} row${n === 1 ? "" : "s"} selected`,
  rowsPerPage: "Rows per page",
  displayedRows: (from, to, count, hasMore) =>
    count !== undefined
      ? `${from}–${to} of ${count}`
      : `${from}–${to} of ${hasMore ? `more than ${to}` : to}`,
  firstPage: "First page",
  previousPage: "Previous page",
  nextPage: "Next page",
  lastPage: "Last page",
  selectAll: "Select all rows",
  selectRow: "Select row",
  expandRow: "Show details",
  collapseRow: "Hide details",
  activeFilters: (n) => `${n} active filter${n === 1 ? "" : "s"}`,
};

export const GRID_LOCALE_TH: GridLocaleText = {
  toolbarColumns: "คอลัมน์",
  toolbarFilters: "ตัวกรอง",
  toolbarDensity: "ความหนาแน่น",
  toolbarExport: "ส่งออก",
  quickFilterPlaceholder: "ค้นหา…",
  quickFilterClear: "ล้างคำค้นหา",
  density: { compact: "กะทัดรัด", standard: "มาตรฐาน", comfortable: "โปร่ง" },
  exportCsv: "ดาวน์โหลดเป็น CSV",
  exportSelected: (n) => `ดาวน์โหลด ${n} แถวที่เลือกเป็น CSV`,
  columnsPanelSearch: "ค้นหาคอลัมน์",
  columnsPanelShowAll: "แสดงทั้งหมด",
  columnsPanelHideAll: "ซ่อนทั้งหมด",
  columnsPanelReset: "คืนค่าเริ่มต้น",
  filterPanelAdd: "เพิ่มตัวกรอง",
  filterPanelRemoveAll: "ลบทั้งหมด",
  filterPanelColumn: "คอลัมน์",
  filterPanelOperator: "เงื่อนไข",
  filterPanelValue: "ค่า",
  filterPanelLogic: "ตรรกะ",
  filterPanelAnd: "และ",
  filterPanelOr: "หรือ",
  filterPanelDelete: "ลบตัวกรอง",
  filterPanelEmpty: "ยังไม่มีตัวกรอง",
  filterValueAny: "ทั้งหมด",
  filterValueTrue: "ใช่",
  filterValueFalse: "ไม่ใช่",
  operators: OPERATORS_TH,
  columnMenu: (c) => `เมนูคอลัมน์ ${c}`,
  columnMenuSortAsc: "เรียงจากน้อยไปมาก",
  columnMenuSortDesc: "เรียงจากมากไปน้อย",
  columnMenuUnsort: "ยกเลิกการเรียง",
  columnMenuFilter: "กรอง",
  columnMenuHide: "ซ่อนคอลัมน์",
  columnMenuManage: "จัดการคอลัมน์",
  columnMenuPinLeft: "ปักหมุดซ้าย",
  columnMenuPinRight: "ปักหมุดขวา",
  columnMenuUnpin: "เลิกปักหมุด",
  columnResize: (c) => `ปรับความกว้าง ${c}`,
  noRows: "ไม่มีข้อมูล",
  noResults: "ไม่พบผลลัพธ์",
  loading: "กำลังโหลด…",
  selectedRows: (n) => `เลือก ${n.toLocaleString()} แถว`,
  rowsPerPage: "แถวต่อหน้า",
  displayedRows: (from, to, count, hasMore) =>
    count !== undefined
      ? `${from}–${to} จาก ${count}`
      : `${from}–${to} จาก ${hasMore ? `มากกว่า ${to}` : to}`,
  firstPage: "หน้าแรก",
  previousPage: "หน้าก่อน",
  nextPage: "หน้าถัดไป",
  lastPage: "หน้าสุดท้าย",
  selectAll: "เลือกทุกแถว",
  selectRow: "เลือกแถว",
  expandRow: "แสดงรายละเอียด",
  collapseRow: "ซ่อนรายละเอียด",
  activeFilters: (n) => `ตัวกรองที่ใช้อยู่ ${n} รายการ`,
};
